import { Flex } from '@weareredlight/design-system'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import unionBy from 'lodash/unionBy'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useParams,
  Outlet,
  useOutletContext,
  useNavigate,
} from 'react-router-dom'

import type { CustomEventType } from 'components/BigCalendar'
import type {
  AppointmentOnCalendarType,
  AppointmentType,
  AppointmentsParams,
} from 'types/appointments'
import type { ProcedureType } from 'types/procedures'
import type { ProviderScheduleType } from 'types/providers'
import type {
  ResourcesParamsType,
  ResourcesType,
  TreatmentType,
} from 'types/treatments'
import type { PaginatedRequest } from 'types/types'

import api from 'api/api'
import AppointmentsCalendar from 'components/Appointments/AppointmentsCalendar'
import Card from 'components/Card'
import PageTemplate from 'components/PageTemplate'
import { useRequest } from 'hooks/useRequest'
import { RootPath } from 'router/enums'
import { TreatmentStatuses } from 'utils/enums'
import { DATE_FORMAT } from 'utils/time'

dayjs.extend(duration)

type ResourcesSelectedType = { room?: string; providers: string[] }

type ContextType = {
  draggedEvent: AppointmentOnCalendarType | null
  treatment: TreatmentType | null
  appointments: PaginatedRequest<AppointmentType>
  isLoading: boolean
  updateTreatment: () => void
  activeProcedure?: { id: string; procedure: ProcedureType }
  resetActiveProcedure: () => void
  reloadAppointmentsList: () => void
  activeAppointment?: AppointmentType
  resources: ResourcesType
  isLoadingResources: boolean
  setResourcesSelected: (key: ResourcesSelectedType) => void
  isCancelledTreatment: boolean
}

export const useSchedule = () => {
  return useOutletContext<ContextType>()
}

const ScheduleAppointment = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { id, treatmentProcedureId } = useParams<{
    id: string
    treatmentProcedureId: string
  }>()

  const [customDate, setCustomDate] = useState(dayjs().format(DATE_FORMAT))

  const [draggedEvent, setDraggedEvent] =
    useState<AppointmentOnCalendarType | null>(null)

  const [resourcesSelected, setResourcesSelected] =
    useState<ResourcesSelectedType>({
      room: undefined,
      providers: [],
    })

  const {
    data: treatment,
    doRequest: getTreatment,
    isLoading: isLoadingTreatment,
  } = useRequest<TreatmentType, { id: string }>(api.getTreatment)

  const {
    data: appointments,
    doRequest: getAppointments,
    isLoading: isLoadingAppointments,
  } = useRequest<PaginatedRequest<AppointmentType>, AppointmentsParams>(
    api.getAppointments,
  )

  const {
    data: resources,
    doRequest: getResources,
    setData: setResources,
    isLoading: isLoadingResources,
  } = useRequest<ResourcesType, ResourcesParamsType>(api.getResources)

  const isLoading = useMemo(
    () => isLoadingAppointments || isLoadingTreatment,
    [isLoadingAppointments, isLoadingTreatment],
  )

  const activeProcedure = useMemo(
    () => treatment?.procedures.find(({ id }) => id === treatmentProcedureId),
    [treatmentProcedureId, treatment?.procedures],
  )

  const activeAppointment = useMemo(() => {
    if (!appointments?.data || !activeProcedure) return undefined

    return appointments.data.find(
      ({ treatmentProcedures }) =>
        treatmentProcedures?.some(tp => tp.id === activeProcedure.id),
    )
  }, [appointments?.data, activeProcedure])

  const procedureIds = useMemo(() => {
    if (activeAppointment?.treatmentProcedures)
      return activeAppointment.treatmentProcedures.map(tp => tp.procedure.id)

    if (activeProcedure?.procedure?.id) {
      return [activeProcedure.procedure.id]
    }

    return []
  }, [activeAppointment?.treatmentProcedures, activeProcedure?.procedure?.id])

  useEffect(() => {
    if (!treatment) return

    let computedDate: string

    if (activeAppointment?.startTime) {
      computedDate = dayjs(activeAppointment.startTime)
        .startOf('week')
        .format(DATE_FORMAT)
    } else if (appointments?.data?.length) {
      computedDate = dayjs(appointments.data[0].startTime).format(DATE_FORMAT)
    } else if (treatment?.treatmentDate) {
      computedDate = dayjs(treatment.treatmentDate).format(DATE_FORMAT)
    } else if (treatment.year && treatment.week) {
      const maxWeeks = dayjs().year(treatment.year).isoWeeksInYear()
      const validWeek = Math.min(treatment.week, maxWeeks)
      computedDate = dayjs()
        .year(treatment.year)
        .startOf('year')
        .add(validWeek - 1, 'week')
        .startOf('week')
        .format(DATE_FORMAT)
    } else {
      computedDate = dayjs().format(DATE_FORMAT)
    }

    setCustomDate(computedDate)

    const startDate = dayjs(computedDate)
      .startOf('week')
      .format('YYYY-MM-DDT00:00:00')
    const endDate = dayjs(computedDate)
      .endOf('week')
      .format('YYYY-MM-DDT00:00:00')

    if (activeProcedure && treatment) {
      getResources({
        organizationId: treatment.organization.id,
        procedureIds,
        id: treatment.id,
        startDateTime: startDate,
        endDateTime: endDate,
      })
    } else {
      setResources(null)
    }
  }, [
    treatment,
    activeAppointment,
    appointments,
    activeProcedure,
    procedureIds,
    getResources,
    setResources,
  ])

  const patientAppointments = useMemo(() => {
    if (!appointments) return []

    const updatedEvents: CustomEventType[] = appointments?.data.map(appt => {
      const isActiveEvent = draggedEvent?.event?.appointment?.id === appt.id
      return {
        title: appt.treatmentProcedures?.[0].procedure.name,
        start: isActiveEvent
          ? draggedEvent?.start
          : dayjs(appt.startTime).toDate(),
        end: isActiveEvent ? draggedEvent?.end : dayjs(appt.endTime).toDate(),
        id: appt.treatmentProcedures?.[0].id,
        appointment: appt,
        procedure: appt.treatmentProcedures?.[0].procedure,
      }
    })

    if (
      activeProcedure &&
      draggedEvent &&
      !draggedEvent?.event?.appointment?.id
    ) {
      updatedEvents.push({
        title: activeProcedure.procedure.name,
        start: draggedEvent?.start,
        end: draggedEvent?.end,
        procedure: activeProcedure.procedure,
        id: activeProcedure.id,
        appointment: null,
      })
    }

    return updatedEvents
  }, [activeProcedure, appointments, draggedEvent])

  const otherAppointments: CustomEventType[] = useMemo(() => {
    const providersAppts = resources?.providers.reduce((acc, cur) => {
      return !resourcesSelected.providers?.includes(cur.id) ||
        !cur?.bookedAppointments
        ? acc
        : [...acc, ...cur.bookedAppointments]
    }, [] as AppointmentType[])
    const roomsAppts = resources?.rooms.reduce((acc, cur) => {
      return resourcesSelected.room !== cur.id || !cur?.bookedAppointments
        ? acc
        : [...acc, ...cur.bookedAppointments]
    }, [] as AppointmentType[])

    const allBooked = unionBy(providersAppts, roomsAppts, 'id') || []

    const mappedAppointments = allBooked.map(appt => ({
      title: appt.treatmentProcedures?.[0].procedure.name,
      start: dayjs(appt.startTime).toDate(),
      end: dayjs(appt.endTime).toDate(),
      id: appt.treatmentProcedures?.[0].id,
      appointment: appt,
      procedure: appt.treatmentProcedures?.[0].procedure,
    }))
    return mappedAppointments
  }, [resources, resourcesSelected])

  const resetActiveProcedure = useCallback(() => {
    navigate(-1)
    setDraggedEvent(null)
  }, [navigate])

  const getAppointmentsCallback = useCallback(() => {
    if (id && treatment?.patient.id) {
      getAppointments({
        treatmentId: id,
        patientId: treatment?.patient.id,
      })
    }
  }, [getAppointments, id, treatment])

  const reloadAppointmentsList = useCallback(() => {
    getAppointmentsCallback()
    navigate(-1)
    setDraggedEvent(null)
  }, [navigate, getAppointmentsCallback])

  const updateTreatment = useCallback(() => {
    if (!id) return
    getTreatment({ id })
  }, [getTreatment, id])

  useEffect(() => {
    updateTreatment()
  }, [updateTreatment])

  useEffect(() => {
    getAppointmentsCallback()
  }, [getAppointmentsCallback])

  const isCancelledTreatment = useMemo(() => {
    return String(treatment?.status) === `${TreatmentStatuses.CANCELLED}`
  }, [treatment?.status])

  const selectedProviderSchedules = useMemo(
    () =>
      resources?.providers.reduce((acc, cur) => {
        return !resourcesSelected.providers?.includes(cur.id)
          ? acc
          : [...acc, ...cur.schedules]
      }, [] as ProviderScheduleType[]) || [],
    [resources?.providers, resourcesSelected.providers],
  )

  const filteredOtherAppointments = useMemo(() => {
    return otherAppointments.filter(
      oa =>
        !patientAppointments.some(
          pa => pa.appointment?.id === oa.appointment?.id,
        ),
    )
  }, [otherAppointments, patientAppointments])

  const mergedAppointments = useMemo(() => {
    return [...patientAppointments, ...filteredOtherAppointments]
  }, [patientAppointments, filteredOtherAppointments])

  return (
    <PageTemplate
      title={t('Appointments Scheduling')}
      goBackTo={RootPath.SCHEDULE}
    >
      <Flex align="start" gap="xxlg" css={{ width: '100%' }}>
        <Flex direction="column" align="start" gap="lg" css={{ width: '40%' }}>
          <Outlet
            context={{
              draggedEvent,
              treatment,
              appointments,
              isLoading,
              activeProcedure,
              resetActiveProcedure,
              reloadAppointmentsList,
              activeAppointment,
              resources,
              isLoadingResources,
              updateTreatment,
              setResourcesSelected,
              isCancelledTreatment,
            }}
          />
        </Flex>
        <Flex css={{ width: '60%' }}>
          <Card extraClasses="wrapper full-width" isLoading={isLoading}>
            {!isLoading && (
              <AppointmentsCalendar
                treatment={treatment}
                activeProcedure={activeProcedure}
                activeAppointment={activeAppointment}
                customDate={customDate}
                setCustomDate={setCustomDate}
                setDraggedEvent={setDraggedEvent}
                isCancelledTreatment={isCancelledTreatment}
                selectedProviderSchedules={selectedProviderSchedules}
                eventsToShow={mergedAppointments}
              />
            )}
          </Card>
        </Flex>
      </Flex>
    </PageTemplate>
  )
}

export default ScheduleAppointment
